import { isJpeg, isPng, readOrientation, stripExif, stripPersonalMetadata, stripPngMetadata } from "./image_metadata.mjs";

// Turns a photo upright and takes the photographer's data out of it before it
// leaves the device.
//
// This has to happen before the upload rather than after, because the CDN caches a
// rendition for a year the first time it is asked for one, and a chat renders the
// message the instant it is sent. A photo uploaded sideways is therefore sideways
// for a year, long after the server has fixed the object behind it. Doing it here
// also means the GPS fix never leaves the phone at all.
//
// Tribute.Jobs.NormalizeMediaWorker does the same job server-side for what is
// already in the bucket, and stays as the safety net for anything that falls back
// to the original below.

const JPEG_QUALITY = 0.92;

// The DM sheet signs images up to a hundred megabytes (`dm_live.ex:60-63`), and
// cleaning one means holding the whole file, then a decoded bitmap and a canvas
// beside it -- hundreds of megabytes for a photo, on a phone, before anything has
// been sent. No camera produces a JPEG near this, so the ceiling only ever turns
// away a file that was going to be trouble; the worker cleans what comes through
// here untouched.
const MAX_BYTES_TO_HOLD = 32_000_000;

/**
 * The bytes to upload in place of `file`. Returns `file` itself whenever there is
 * nothing to do or anything at all goes wrong -- failing to normalise a photo is
 * worth far less than failing to send it.
 */
export async function normalizeForUpload(file, maxFileSize) {
    try {
        if (!file.type.startsWith("image/")) return file;
        if (file.size > MAX_BYTES_TO_HOLD) return file;

        const bytes = new Uint8Array(await file.arrayBuffer());

        if (isPng(bytes)) return replacement(stripPngMetadata(bytes), bytes, file, maxFileSize);
        if (!isJpeg(bytes)) return file;

        const orientation = readOrientation(bytes);

        // Already upright, so there is nothing worth decoding for: cut the personal
        // blocks out of the file and leave the compressed image untouched. The stored
        // photo stays pixel for pixel the one that was taken.
        if (!orientation || orientation === 1) return replacement(stripPersonalMetadata(bytes), bytes, file, maxFileSize);

        return within(await rotate(bytes, orientation, file.type), file, maxFileSize);
    } catch {
        return file;
    }
}

// Turning the pixels means decoding them, and re-encoding afterwards drops every
// block of metadata on its own -- a canvas has nowhere to keep one. The decoder is
// handed a copy with no EXIF because browsers disagree about whether they orient an
// image themselves, and one that does would turn the photo a second time.
async function rotate(bytes, orientation, type) {
    const bitmap = await createImageBitmap(new Blob([stripExif(bytes)], { type }));

    try {
        const quarterTurn = orientation >= 5;
        const canvas = document.createElement("canvas");
        canvas.width = quarterTurn ? bitmap.height : bitmap.width;
        canvas.height = quarterTurn ? bitmap.width : bitmap.height;

        const context = canvas.getContext("2d");
        context.transform(...transformFor(orientation, bitmap.width, bitmap.height));
        context.drawImage(bitmap, 0, 0);

        return await new Promise((resolve, reject) => {
            canvas.toBlob((result) => (result ? resolve(result) : reject(new Error("canvas produced no blob"))), type, JPEG_QUALITY);
        });
    } finally {
        bitmap.close?.();
    }
}

/**
 * The canvas matrix that lands a source of `width` x `height` the way its EXIF
 * orientation says it should be seen. Exported so the eight cases can be checked
 * without a browser; they mirror the jpegtran flags in Tribute.Clients.Jpegtran.
 */
export function transformFor(orientation, width, height) {
    switch (orientation) {
        case 2:
            return [-1, 0, 0, 1, width, 0];
        case 3:
            return [-1, 0, 0, -1, width, height];
        case 4:
            return [1, 0, 0, -1, 0, height];
        case 5:
            return [0, 1, 1, 0, 0, 0];
        case 6:
            return [0, 1, -1, 0, height, 0];
        case 7:
            return [0, -1, -1, 0, height, width];
        case 8:
            return [0, -1, 1, 0, 0, width];
        default:
            return [1, 0, 0, 1, 0, 0];
    }
}

// Nothing was taken out, so there is no reason to hand the uploader a copy.
function replacement(stripped, original, file, maxFileSize) {
    if (stripped === original) return file;

    return within(new Blob([stripped], { type: file.type }), file, maxFileSize);
}

// The presigned form pins the content type exactly and signs a size ceiling, so a
// rewrite that outgrew the limit is one S3 would reject.
function within(candidate, file, maxFileSize) {
    return maxFileSize && candidate.size > maxFileSize ? file : candidate;
}
